import type { LogContext } from './loggerUtils';
import type { BaseErrorContext } from './errorContext';

interface SerializedError {
  name: string;
  message: string;
  stack?: string;
  code?: string | number;
  cause?: SerializedError;
  context?: Record<string, any>;
  timestamp: number;
  [key: string]: any;
}

/**
 * Serializes an error object into a plain object that can be safely logged
 * and preserves all relevant error information.
 */
export function serializeError(error: unknown, context?: LogContext): SerializedError {
  // Handle non-Error objects
  if (!(error instanceof Error)) {
    return {
      name: 'UnknownError',
      message: String(error),
      context,
      timestamp: Date.now(),
    };
  }

  // Start with basic error properties
  const serialized: SerializedError = {
    name: error.name,
    message: error.message,
    stack: error.stack,
    context,
    timestamp: Date.now(),
  };

  // Add error code if present
  if ('code' in error && (typeof error.code === 'string' || typeof error.code === 'number')) {
    serialized.code = error.code;
  }

  // Add error cause if present
  if (error.cause) {
    serialized.cause = serializeError(error.cause);
  }

  // Add any custom properties
  for (const [key, value] of Object.entries(error)) {
    if (!['name', 'message', 'stack', 'cause'].includes(key)) {
      try {
        // Attempt to serialize the value
        const serializedValue = JSON.parse(JSON.stringify(value));
        serialized[key] = serializedValue;
      } catch {
        // If value can't be serialized, convert to string
        serialized[key] = String(value);
      }
    }
  }

  return serialized;
}

/**
 * Creates an error chain string for logging purposes.
 * Example: "Error: Network error -> TypeError: Failed to fetch -> Error: No internet"
 */
export function getErrorChain(error: unknown): string {
  const chain: string[] = [];
  let current: any = error;

  while (current) {
    if (current instanceof Error) {
      chain.push(`${current.name}: ${current.message}`);
    } else {
      chain.push(String(current));
    }
    current = current.cause;
  }

  return chain.join(' -> ');
}

/**
 * Extracts a concise error message from an error object or chain of errors.
 * Useful for user-facing error messages.
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    // If there's a cause chain, include the root cause
    if (error.cause) {
      const rootCause = getRootCause(error);
      return `${error.message} (Caused by: ${rootCause.message})`;
    }
    return error.message;
  }
  return String(error);
}

/**
 * Gets the root cause of an error chain.
 */
export function getRootCause(error: unknown): Error {
  let current: any = error;
  while (current?.cause) {
    current = current.cause;
  }
  return current instanceof Error ? current : new Error(String(current));
}

/**
 * Checks if an error matches a specific error code.
 */
export function hasErrorCode(error: unknown, code: string | number): boolean {
  if (error && typeof error === 'object' && 'code' in error) {
    return error.code === code;
  }
  return false;
}

/**
 * Wraps an error with additional context while preserving the original error chain.
 */
export function wrapError(
  originalError: unknown,
  message: string,
  context?: Record<string, any>
): Error {
  const wrappedError = new Error(message);
  wrappedError.cause = originalError;

  if (context) {
    Object.assign(wrappedError, { context });
  }

  return wrappedError;
}

/**
 * Creates a detailed error report including context and metrics.
 */
export function createErrorReport(error: unknown, context: BaseErrorContext): Record<string, any> {
  const serialized = serializeError(error);

  return {
    error: serialized,
    context: {
      ...context,
      errorChain: context.errorChain.map(entry => ({
        message: entry.message,
        timestamp: entry.timestamp,
        timeSinceFirst: entry.timestamp - context.errorChain[0].timestamp,
      })),
    },
    metrics: {
      totalDuration: Date.now() - context.timestamp,
      retryCount: context.retryCount,
      recoveryAttempts: context.recoveryAttempts,
      severity: context.severity,
      impact: context.impact,
      category: context.errorCategory,
    },
    timestamp: Date.now(),
  };
}
